import React, { useCallback, useEffect, useRef, useState } from 'react';
import media from 'utils/mediaStyle';
import FooterCheckout from './Footer';
import StepRow, { StepBox } from './StepRow';
import Value, { SupplierName } from './Value';

import { Button as AButton, Modal as AModal, Input, Spin } from 'antd';
import { ErrorBoundary } from 'components/ErrorBoundary';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import { Viewport } from 'components/ViewportProvider';
import useMessages from 'containers/CheckoutPage/hooks/useMessages';
import messages from 'containers/CheckoutPage/messages';
import DeliveryDateTime from 'containers/DeliveryDateTime';
import moment from 'moment';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import translations from 'translations';
import { Voucher } from 'types/schema';
import utilsMessages from 'utils/messages';
import triggerGA4EcommerceEvent, { EventName } from 'utils/triggerGA4Event';
import { VOUCHER_STATUS } from '../../containers/CheckoutPage/constants';
import Button from '../Button/Button';
import Checkbox from '../Checkbox/Checkbox';
import SubplierMark from '../SubplierMark/SubplierMark';
import Label from './Label';
import ModalNote from './ModalNote';
import StepCol from './StepCol';
import StepContainer from './StepContainer';
import StepItem from './StepItem';
import DangerIcon from './danger.svg';

const ConfirmSameDayDeliveryModal = styled(AModal)`
  .ant-modal-content {
    padding: 24px 16px 12px;
  }
  .ant-modal-title {
    font-size: 24px;
    font-weight: 500;
    color: #595959;
    margin-bottom: 20px !important;
  }
  .ant-modal-footer {
    display: flex;
    justify-content: center;
    button {
      flex-grow: 1;
    }
    margin-top: 20px !important;
  }
`;

const ConfirmModalTitle = styled.div`
  margin: -8px -16px 0;
  padding: 0 16px 16px;
  border-bottom: 1px solid #e0e0e0;
`;
const NextStepButton = styled(AButton)`
  border-radius: 4px !important;
`;
const ConfirmContentModal = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DeliveryDateTimeWrapper = styled.div`
  flex: 1;
`;

const InvoiceNoteContainer = styled.div`
  width: 100%;
  margin-top: 10pt;
  padding-left: 4px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${media.md`
    width: auto;
  `}
`;

const InvoiceNoteInput = styled(Input)`
  width: 100%;
  height: 34px !important;
  border-radius: 6px !important;
  padding: 4px 11px 6px !important;

  ${media.md`
    width: 350px;
  `}

  .ant-input-show-count-suffix {
    font-weight: 400 !important;
  }
`;

export default ({
  loading,
  createOrderDraftLoading,
  cart,
  deliveryFee,
  checkout,
  onGoPreviousStep,
  onCreateOrderDraft,
  onChangeDeliveryDate,
  onChangeDeliveryTime,
  onSaveNote,
  onUpdateDeliveryNote,
  setOpenConfirmSameDayDeliveryModal,
  orderDisabled,
  openConfirmSameDayDeliveryModal,
  voucherStatus,
  currentAppliedVoucher,
  discountedValue: discountValue,
  getVouchers,
  invoiceNote,
  onSaveInvoiceNote,
  onChangeInvoiceNote,
}: {
  loading: boolean;
  createOrderDraftLoading: boolean;
  checkout: any;
  cart: { cartSet: any[] };
  deliveryFee: any;
  onGoPreviousStep: () => void;
  onCreateOrderDraft: () => void;
  onChangeDeliveryDate: (data: any) => void;
  onChangeDeliveryTime: (data: any) => void;
  onSaveNote: (data: any) => void;
  onUpdateDeliveryNote: (data: any) => void;
  setOpenConfirmSameDayDeliveryModal: (data: boolean) => void;
  orderDisabled: boolean;
  openConfirmSameDayDeliveryModal: boolean;
  voucherStatus: VOUCHER_STATUS | null;
  currentAppliedVoucher: Voucher | null;
  discountedValue: number;
  getVouchers: (data: any) => void;
  invoiceNote?: string;
  onSaveInvoiceNote: (data: any) => void;
  onChangeInvoiceNote: (data?: string) => void;
}) => {
  const [isOpenNoteModal, onOpenNoteModal] = useState(0);
  const [isOpenCalendar, setOpenCalendar] = useState(false);
  const [reuseNote, onReuseNote] = useState(false);
  const deliveryNotes = checkout.deliveryNotes;
  const { contextHolder, handleShowSuccess, handleShowError } = useMessages();
  const prevAppliedVoucherRef = useRef(currentAppliedVoucher);

  useEffect(() => {
    if (voucherStatus && prevAppliedVoucherRef.current !== currentAppliedVoucher) {
      if (voucherStatus === VOUCHER_STATUS.APPLIED && currentAppliedVoucher) {
        handleShowSuccess();
      } else {
        handleShowError();
      }
    }
    prevAppliedVoucherRef.current = currentAppliedVoucher;
  }, [voucherStatus, currentAppliedVoucher]);

  const onChangeDate = (supplier: any) => (date: any) => {
    onChangeDeliveryDate({
      supplierId: supplier.id,
      date: moment(date).format('YYYY-MM-DD'),
    });
  };

  const onChangeTime = (supplier: any) => (time: any) => {
    onChangeDeliveryTime({
      supplierId: supplier.id,
      time: time,
    });
  };

  const onAddNote = (text: any) => {
    if (reuseNote) {
      onSaveNote({ text: text });
    }
    const item = cart.cartSet[isOpenNoteModal - 1];
    onUpdateDeliveryNote({
      supplierId: item.supplier.id,
      note: text,
    });
    onOpenNoteModal(0);
  };

  const onCheckDeliveryNote = (item: any) => (text: string) => (event: any) => {
    onUpdateDeliveryNote({
      supplierId: item.supplier.id,
      note: text,
      deleted: !event.target.checked,
    });
  };

  const onCreateOrder = useCallback(() => {
    if (openConfirmSameDayDeliveryModal) {
      setOpenConfirmSameDayDeliveryModal(false);
    }
    onCreateOrderDraft();
    triggerGA4EcommerceEvent(EventName.ADD_SHIPPING_INFO, {
      ...cart.cartSet[0],
    });
  }, [cart, onCreateOrderDraft, openConfirmSameDayDeliveryModal, setOpenConfirmSameDayDeliveryModal]);

  const proceedToCreateOrder = useCallback(() => {
    let isSameDayDelivery = false;
    for (const cartItem of cart.cartSet) {
      const deliveryDay = checkout.deliveryDate[cartItem.supplier.id];
      if (moment(deliveryDay, 'DD/MM/YYYY').isSame(moment(), 'day')) {
        isSameDayDelivery = true;
      } else {
        isSameDayDelivery = false;
      }
    }
    if (isSameDayDelivery) {
      setOpenConfirmSameDayDeliveryModal(true);
      return;
    }
    onCreateOrder();
  }, [cart, checkout?.deliveryDate, onCreateOrder, setOpenConfirmSameDayDeliveryModal]);

  const handleRedirectToStep3 = useCallback(() => {
    onSaveInvoiceNote({
      note: invoiceNote,
      callback: (success: boolean) => {
        if (success) {
          proceedToCreateOrder();
        }
      },
    });
  }, [invoiceNote, onSaveInvoiceNote, proceedToCreateOrder]);

  const notIncludeAlcoholProduct = () => {
    return cart?.cartSet?.every((item) => {
      const isIncludesAlcoholProduct = item?.items?.some((it) => it?.product?.category?.parent?.name === 'Alcohol');
      return !isIncludesAlcoholProduct;
    });
  };
  return (
    <Viewport.Consumer>
      {({ width }) => {
        const isMobileSize = width < 786;
        return (
          <>
            {contextHolder}
            <StepContainer>
              <Spin spinning={loading}>
                {cart.cartSet.map((sup: any, index) => {
                  if (sup.deliveryTimeSlots) {
                    const specificNotes: string[] = [];
                    sup.deliveryNotes.forEach((dn: string) => {
                      const note = deliveryNotes.find((n) => n.text === dn);
                      if (!note) {
                        specificNotes.push(dn);
                      }
                    });

                    return (
                      <ErrorBoundary key={`step-2-${index}`}>
                        <StepItem>
                          <StepBox>
                            <StepRow>
                              <StepCol span={2}>
                                {!isMobileSize && (
                                  <Label>
                                    <FormattedMessage {...utilsMessages.supplierName} />
                                  </Label>
                                )}
                                <SupplierName>
                                  <SubplierMark
                                    className={sup.supplier.private ? 'unregistered' : ''}
                                    style={{ width: '16px', marginRight: '3pt' }}
                                  />{' '}
                                  {sup.supplier.name}
                                </SupplierName>
                              </StepCol>
                              <StepCol className="hideOnMobile">
                                <Label>
                                  <FormattedMessage {...utilsMessages.totalItem} />
                                </Label>
                                <Value>
                                  {sup.items.length}
                                  &nbsp;
                                  <FormattedMessage {...utilsMessages.items} />
                                </Value>
                              </StepCol>
                              {!isMobileSize && (
                                <>
                                  <StepCol>
                                    <Label>
                                      <FormattedMessage {...utilsMessages.earliest} />
                                    </Label>
                                    <Value>{moment(sup.earliestDate).format('DD/MM/YYYY')}</Value>
                                  </StepCol>
                                  <StepCol>
                                    <Label>
                                      <FormattedMessage {...utilsMessages.paymentMethod} />
                                    </Label>
                                    <Value>
                                      {notIncludeAlcoholProduct() ? (
                                        <FormattedMessage {...utilsMessages[sup.paymentMethod]} />
                                      ) : (
                                        <FormattedMessage {...utilsMessages.BANK_TRANSFER} />
                                      )}
                                    </Value>
                                  </StepCol>
                                </>
                              )}
                            </StepRow>
                            {isMobileSize && (
                              <StepRow className="justify-content-between">
                                <StepCol className="flex-initial">
                                  <Label>
                                    <FormattedMessage {...utilsMessages.earliest} />
                                  </Label>
                                  <Value>{moment(sup.earliestDate).format('DD/MM/YYYY')}</Value>
                                </StepCol>
                                <StepCol className="flex-initial">
                                  <Label>
                                    <FormattedMessage {...utilsMessages.paymentMethod} />
                                  </Label>
                                  <Value>
                                    {notIncludeAlcoholProduct() ? (
                                      <FormattedMessage {...utilsMessages[sup.paymentMethod]} />
                                    ) : (
                                      <FormattedMessage {...utilsMessages.BANK_TRANSFER} />
                                    )}
                                  </Value>
                                </StepCol>
                              </StepRow>
                            )}
                          </StepBox>
                          <StepBox>
                            <StepRow gap={isMobileSize ? '20px' : '60px'}>
                              <DeliveryDateTimeWrapper>
                                {!!checkout.deliveryDate[sup.supplier.id] && (
                                  <DeliveryDateTime
                                    supplierId={sup.supplier.id}
                                    deliveryDate={checkout.deliveryDate[sup.supplier.id]}
                                    deliveryTime={checkout.timeSlot[sup.supplier.id]}
                                    onDeliveryDateChange={onChangeDate(sup.supplier)}
                                    onDeliveryTimeChange={onChangeTime(sup.supplier)}
                                    isOpenCalendar={isOpenCalendar}
                                    setOpenCalendar={setOpenCalendar}
                                  />
                                )}
                                <InvoiceNoteContainer>
                                  <Label>
                                    <FormattedMessage {...utilsMessages.invoiceNote} />
                                  </Label>
                                  <InvoiceNoteInput
                                    placeholder={translations(utilsMessages.invoiceNotePlaceholder)}
                                    value={invoiceNote}
                                    onChange={(e) => onChangeInvoiceNote(e.target.value)}
                                    count={{
                                      show: true,
                                      max: 200,
                                      strategy: (txt) => txt?.length,
                                      exceedFormatter: (txt, { max }) => txt?.slice(0, max),
                                    }}
                                  />
                                </InvoiceNoteContainer>
                              </DeliveryDateTimeWrapper>
                              <StepCol span={1}>
                                <Label>
                                  <FormattedMessage {...utilsMessages.noteForDelivery} />
                                </Label>
                                {deliveryNotes?.map((item: any) => (
                                  <div key={item.id}>
                                    <Checkbox
                                      size="small"
                                      checked={sup.deliveryNotes.indexOf(item.text) > -1}
                                      onChange={onCheckDeliveryNote(sup)(item.text)}
                                    >
                                      {item.text}
                                    </Checkbox>
                                  </div>
                                ))}
                                {specificNotes?.map((text: any) => (
                                  <div key={text}>
                                    <Checkbox size="small" checked={true} onChange={onCheckDeliveryNote(sup)(text)}>
                                      {text}
                                    </Checkbox>
                                  </div>
                                ))}
                                <Button
                                  type="link"
                                  size="small"
                                  onClick={() => {
                                    onOpenNoteModal(index + 1);
                                  }}
                                  style={{
                                    padding: 0,
                                  }}
                                >
                                  {'+ '}
                                  <FormattedMessage {...utilsMessages.addNote} />
                                </Button>
                              </StepCol>
                            </StepRow>
                          </StepBox>
                        </StepItem>
                      </ErrorBoundary>
                    );
                  }

                  return null;
                })}
              </Spin>
            </StepContainer>
            <FooterCheckout
              orderDisabled={orderDisabled || loading}
              onGoNext={handleRedirectToStep3}
              deliveryFee={deliveryFee}
              subTotal={checkout?.total}
              onGoBack={onGoPreviousStep}
              cart={cart}
              loading={createOrderDraftLoading}
              isMobile={isMobileSize}
              voucherStatus={voucherStatus}
              getVouchers={getVouchers}
              discountValue={discountValue}
            />
            <ModalNote
              headerLabel={<FormattedMessage {...utilsMessages.noteForDelivery} />}
              isOpen={!!isOpenNoteModal}
              onSubmit={onAddNote}
              onClose={() => onOpenNoteModal(0)}
              note={''}
            >
              <Checkbox checked={reuseNote} onChange={() => onReuseNote(!reuseNote)}>
                <FormattedMessage {...messages.saveDeliveryNote} />
              </Checkbox>
            </ModalNote>
            <ConfirmSameDayDeliveryModal
              open={openConfirmSameDayDeliveryModal}
              title={
                <ConfirmModalTitle>
                  <FormattedMessage {...utilsMessages.notice} />
                </ConfirmModalTitle>
              }
              centered
              onOk={() => {
                setOpenCalendar(true);
                setOpenConfirmSameDayDeliveryModal(false);
              }}
              onCancel={() => setOpenConfirmSameDayDeliveryModal(false)}
              closable={false}
              okText={translations(utilsMessages.changeDay)}
              okButtonProps={{
                size: 'large',
                style: {
                  borderRadius: '4px',
                },
              }}
              cancelButtonProps={{
                size: 'large',
                style: {
                  borderRadius: '4px',
                },
              }}
              footer={(_, { OkBtn }) => (
                <>
                  <NextStepButton size="large" onClick={onCreateOrder}>
                    <FormattedMessage {...utilsMessages.confirm} />
                  </NextStepButton>
                  <OkBtn />
                </>
              )}
            >
              <ConfirmContentModal>
                <SVGIcon src={DangerIcon} />
                <FormattedMessage {...utilsMessages.confirmSamDayDelivery} />
              </ConfirmContentModal>
            </ConfirmSameDayDeliveryModal>
          </>
        );
      }}
    </Viewport.Consumer>
  );
};
